import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Cpu, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageShell, PageHero, PageSection } from "../components/PageShell";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import FormModal from "../components/FormModal";
import { adminService } from "../services/adminService";

function StatusBadge({ status }) {
  const styles = {
    available: "bg-success/15 text-success",
    claimed: "bg-accent/15 text-accent",
    disabled: "bg-muted text-muted-foreground",
  };
  return (
    <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-wider ${styles[status] || styles.disabled}`}>
      {status}
    </span>
  );
}

export default function SuperAdminDevicePoolPage() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [singleId, setSingleId] = useState("");
  const [bulkIds, setBulkIds] = useState("");
  const [batchLabel, setBatchLabel] = useState("");
  const [filter, setFilter] = useState("all");

  async function load() {
    setError("");
    const res = await adminService.listDevicePool();
    if (!res.ok) {
      setError(res.error || "Failed to load device pool");
      toast.error(res.error || "Failed to load device pool");
      return;
    }
    setItems(res.data?.items || []);
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((x) => x.status === filter);
  }, [filter, items]);

  const stats = useMemo(() => ({
    total: items.length,
    available: items.filter((x) => x.status === "available").length,
    claimed: items.filter((x) => x.status === "claimed").length,
  }), [items]);

  async function provisionIds(ids) {
    const normalized = ids.map((x) => String(x || "").trim()).filter(Boolean);
    if (!normalized.length) {
      toast.warning("Enter at least one device ID");
      return;
    }
    const res = await adminService.provisionDevicePool({
      deviceIds: normalized,
      batchLabel: batchLabel.trim(),
    });
    if (!res.ok) {
      toast.error(res.error || "Provision failed");
      return;
    }
    const results = res.data?.results || [];
    const created = results.filter((r) => r.ok).length;
    const failed = results.filter((r) => !r.ok).length;
    toast.success(`Provisioned ${created} device ID(s)${failed ? `, ${failed} skipped` : ""}`);
    setAddOpen(false);
    setSingleId("");
    setBulkIds("");
    await load();
  }

  return (
    <PageShell>
      <PageHero
        eyebrow="Super Admin"
        title="Device ID"
        highlight="pool."
        description="Register device IDs before users can link them. Each ID can belong to only one user."
      />
      <PageSection variant="content">
        <div className="mx-auto max-w-6xl grid gap-6">
          <div className="flex flex-wrap gap-2">
            <Link to="/super-admin" className="px-4 py-2 rounded-full border border-border text-sm">Back to Super Admin</Link>
            <Button onClick={() => setAddOpen(true)}><Plus className="h-4 w-4" /> Register device IDs</Button>
          </div>

          {error ? <Card><CardContent className="py-4 text-sm text-destructive">{error}</CardContent></Card> : null}

          <div className="grid sm:grid-cols-3 gap-4">
            <Card><CardContent className="py-5"><div className="text-xs text-muted-foreground">Total registered</div><div className="font-display text-3xl mt-1">{stats.total}</div></CardContent></Card>
            <Card><CardContent className="py-5"><div className="text-xs text-muted-foreground">Available to claim</div><div className="font-display text-3xl mt-1">{stats.available}</div></CardContent></Card>
            <Card><CardContent className="py-5"><div className="text-xs text-muted-foreground">Linked to users</div><div className="font-display text-3xl mt-1">{stats.claimed}</div></CardContent></Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between gap-3 flex-wrap">
                <span className="flex items-center gap-2"><Cpu className="h-5 w-5 text-accent" /> Registered IDs</span>
                <select className="h-9 rounded-xl border border-border bg-background px-3 text-sm" value={filter} onChange={(e) => setFilter(e.target.value)}>
                  <option value="all">All</option>
                  <option value="available">Available</option>
                  <option value="claimed">Claimed</option>
                  <option value="disabled">Disabled</option>
                </select>
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3">
              {filtered.map((d) => (
                <div key={d.deviceId} className="rounded-2xl border border-border p-4 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-medium">{d.deviceId}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {d.status === "claimed" ? `Owner: ${d.ownerUid}` : "Ready for user to link"}
                      {d.batchLabel ? ` · Batch: ${d.batchLabel}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={d.status} />
                    {d.status === "available" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          const res = await adminService.deleteDevicePoolId(d.deviceId);
                          if (!res.ok) return toast.error(res.error || "Delete failed");
                          toast.success("Device ID removed from pool");
                          await load();
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
              {!filtered.length ? <div className="text-sm text-muted-foreground">No device IDs in this filter.</div> : null}
            </CardContent>
          </Card>
        </div>
      </PageSection>

      <FormModal open={addOpen} title="Register device IDs" onClose={() => setAddOpen(false)}>
        <div className="grid gap-3 text-sm">
          <p className="text-muted-foreground">
            Flash each physical device with its matching ID before the user links it. Users can only connect IDs that exist in this list.
          </p>
          <input
            className="h-10 px-3 rounded-xl border border-border bg-background"
            placeholder="Single device ID (e.g. O3)"
            value={singleId}
            onChange={(e) => setSingleId(e.target.value)}
          />
          <textarea
            className="min-h-28 px-3 py-2 rounded-xl border border-border bg-background"
            placeholder="Bulk IDs — one per line or comma-separated&#10;O4, O5, O6"
            value={bulkIds}
            onChange={(e) => setBulkIds(e.target.value)}
          />
          <input
            className="h-10 px-3 rounded-xl border border-border bg-background"
            placeholder="Batch label (optional, e.g. May-2026-batch)"
            value={batchLabel}
            onChange={(e) => setBatchLabel(e.target.value)}
          />
          <Button
            onClick={() => {
              const bulk = bulkIds.split(/[\n,]+/).map((x) => x.trim()).filter(Boolean);
              const ids = singleId.trim() ? [singleId.trim(), ...bulk] : bulk;
              provisionIds([...new Set(ids)]);
            }}
          >
            Register IDs
          </Button>
        </div>
      </FormModal>
    </PageShell>
  );
}
